import { createSelector } from 'reselect'
import { StateWithAppNavigation } from '../types'
import { BottomNavState } from './types'

// Constants for tab limits
const MAX_TABS = 5
const MIN_TABS = 2

// Base selector for the bottom nav sub-slice
export const selectBottomNav = (
  state: StateWithAppNavigation
): BottomNavState => state.appNavigation.bottomNav

// Memoized selectors using createSelector for better performance
export const selectNavigationTabs = createSelector(
  [selectBottomNav],
  bottomNav =>
    Object.values(bottomNav.navigation_tabs).sort(
      (a, b) => a.position - b.position
    )
)

export const selectBottomNavHasChanges = createSelector(
  [selectBottomNav],
  bottomNav => bottomNav.hasChanges
)

export const selectTabCount = createSelector(
  [selectBottomNav],
  bottomNav => Object.keys(bottomNav.navigation_tabs).length
)

// Selector to check if the Redux store has been populated with navigation tabs
export const selectHasNavigationTabs = createSelector(
  [selectBottomNav],
  bottomNav => Object.keys(bottomNav.navigation_tabs).length > 0
)

// Selector to get a specific tab by ID
export const selectNavigationTabById = createSelector(
  [
    selectBottomNav,
    (_state: StateWithAppNavigation, tabId: string | number) => tabId,
  ],
  (bottomNav, tabId) => {
    return bottomNav?.navigation_tabs?.[String(tabId)]
  }
)

// Selector to get ordered tab IDs only (for efficient rendering)
export const selectNavigationTabIds = createSelector(
  [selectBottomNav],
  bottomNav =>
    Object.values(bottomNav.navigation_tabs)
      .sort((a, b) => a.position - b.position)
      .map(tab => tab.id)
      .filter((id): id is string | number => id !== undefined)
)

// Selector for delete modal state
export const selectDeleteModal = createSelector(
  [selectBottomNav],
  bottomNav => bottomNav.deleteModal
)

// Selector to check if maximum tabs limit has been reached
export const selectMaxTabsReached = createSelector(
  [selectBottomNav],
  bottomNav => Object.keys(bottomNav.navigation_tabs).length >= MAX_TABS
)

// Selector to check if a tab can be deleted by tabId
export const selectCanDeleteTab = createSelector(
  [
    selectBottomNav,
    (_state: StateWithAppNavigation, tabId: string | number) => tabId,
  ],
  (bottomNav, tabId) => {
    const tab = bottomNav.navigation_tabs[String(tabId)]
    if (!tab) return false

    const tabCount = Object.keys(bottomNav.navigation_tabs).length

    // Cannot delete if we're at minimum tabs
    if (tabCount <= MIN_TABS) return false

    // First and last tabs cannot be deleted
    if (tab.position === 1) return false
    if (tab.position === tabCount) return false
    return true
  }
)

// Selector to check if a tab can be moved up by tabId
export const selectCanMoveTabUp = createSelector(
  [
    selectBottomNav,
    (_state: StateWithAppNavigation, tabId: string | number) => tabId,
  ],
  (bottomNav, tabId) => {
    const tab = bottomNav.navigation_tabs[String(tabId)]
    if (!tab) return false

    const tabCount = Object.keys(bottomNav.navigation_tabs).length
    // First tab cannot move up
    if (tab.position === 1) return false
    // Last tab cannot move up
    if (tab.position === tabCount) return false
    // Second tab cannot move up (would move first tab down)
    if (tab.position === 2) return false
    return true
  }
)

// Selector to check if a tab can be moved down by tabId
export const selectCanMoveTabDown = createSelector(
  [
    selectBottomNav,
    (_state: StateWithAppNavigation, tabId: string | number) => tabId,
  ],
  (bottomNav, tabId) => {
    const tab = bottomNav.navigation_tabs[String(tabId)]
    if (!tab) return false

    const tabCount = Object.keys(bottomNav.navigation_tabs).length
    // First tab cannot move down
    if (tab.position === 1) return false
    // Last tab cannot move down
    if (tab.position === tabCount) return false
    // Second-to-last tab cannot move down (would move last tab up)
    if (tab.position === tabCount - 1) return false
    return true
  }
)
